/** Job workspace: map + measurement beside takeoff, verification, bid math, inventory (§4.3, §4.4). */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { putRecord, createRecord, softDelete, putSettings, getRecord } from '../../core/db';
import { fmtCents, fmtQty, toCents } from '../../core/money';
import { computeBid, resolveLines } from '../../docs/compute';
import { nowIso } from '../../core/ids';
import { polygonAreaSqFt, lineLengthFt, perimeterFt, type LatLng } from '../../core/geo';
import type { InventoryItem, JobStatus, PriceKind, TakeoffLine } from '../../core/types';
import { clientsSig, jobBundleSig, loadJobBundle, pricesSig, refreshCore, settingsSig, toast } from '../state';
import { InventoryPanel } from './InventoryPanel';
import { Empty, FlagLine, SourceChip, STATUS_LABELS, Tie } from '../bits';
import { MapPane, type ArmedTool, type ShapeView } from '../../map/MapPane';
import { IntakeBar } from './IntakeBar';
import { FindBar } from './FindBar';
import { TractFinder } from './TractFinder';
import { lookupParcel, looksLikeOneHouse } from '../../providers/parcel';
import { DocsPanel } from './DocsPanel';
import { AiPanel } from './AiPanel';

/** quantity a finished drawing yields, by price kind */
export function measureToQty(kind: PriceKind, coords: LatLng[]): { qty: number; unit: 'sqft' | 'linft' | 'count' } {
  if (kind === 'area') return { qty: Math.round(polygonAreaSqFt(coords)), unit: 'sqft' };
  if (kind === 'line') return { qty: Math.round(lineLengthFt(coords)), unit: 'linft' };
  return { qty: coords.length, unit: 'count' };
}

export function JobView({ jobId }: { jobId: string }) {
  const [addItem, setAddItem] = useState('');
  const [addQty, setAddQty] = useState('');
  const [armedKey, setArmedKey] = useState('');
  const [draftPts, setDraftPts] = useState<LatLng[]>([]);
  // authoritative draft: updated synchronously in the map click callback so Finish
  // never reads a render-lagged state (found by the repeated-e2e race)
  const draftRef = useRef<LatLng[]>([]);
  const updateDraft = (pts: LatLng[]) => { draftRef.current = pts; setDraftPts(pts); };
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [viewBox, setViewBox] = useState<[number, number, number, number] | null>(null);

  useEffect(() => { void loadJobBundle(jobId); }, [jobId]);
  const b = jobBundleSig.value;
  const prices = pricesSig.value;
  const settings = settingsSig.value;

  const armed: ArmedTool = useMemo(() => {
    if (armedKey === 'boundary') return { kind: 'boundary' };
    const p = prices.find((x) => x.itemId === armedKey && x.kind !== 'service');
    return p ? { kind: 'item', itemId: p.itemId, priceKind: p.kind as 'area' | 'line' | 'count', name: p.name } : null;
  }, [armedKey, prices]);

  if (!b || b.job.id !== jobId) return <div class="muted">Loading job…</div>;
  const { job, lines, inventory, shapes } = b;
  const user = settings?.currentUser ?? 'david';
  const resolved = resolveLines(lines, prices);
  const bid = computeBid(resolved, job.fin);
  const byId = new Map(lines.map((l) => [l.id, l]));
  const resolvedIds = new Set(resolved.map((r) => r.takeoffLineId));
  const orphans = lines.filter((l) => !l.deletedAt && !resolvedIds.has(l.id));
  const lineByShape = new Map(lines.filter((l) => l.shapeId).map((l) => [l.shapeId!, l]));
  const shapeViews: ShapeView[] = shapes.map((s) => {
    const l = lineByShape.get(s.id);
    return { ...s, itemId: l?.priceItemId, verified: l?.verification.verified ?? false, label: l?.label ?? '' };
  });

  async function reload() { await loadJobBundle(jobId); }

  function toggleVerify(l: TakeoffLine) {
    const v = !l.verification.verified;
    const next = { ...l, verification: { verified: v, by: v ? user : undefined, at: v ? nowIso() : undefined, note: l.verification.note } };
    // optimistic: paint the tie immediately (§7 100ms budget), then persist and re-sync
    if (b) jobBundleSig.value = { ...b, lines: b.lines.map((x) => (x.id === l.id ? next : x)) };
    void putRecord('takeoffLines', next).then(reload);
  }

  function toggleVerifyInv(it: InventoryItem) {
    const v = !it.verification.verified;
    const next = { ...it, verification: { verified: v, by: v ? user : undefined, at: v ? nowIso() : undefined, note: it.verification.note } };
    if (b) jobBundleSig.value = { ...b, inventory: b.inventory.map((x) => (x.id === it.id ? next : x)) };
    void putRecord('inventoryItems', next).then(reload);
  }

  async function setQty(l: TakeoffLine, qty: number) {
    if (!Number.isFinite(qty) || qty < 0) return;
    await putRecord('takeoffLines', {
      ...l, qty,
      provenance: { ...l.provenance, source: 'manual', setBy: user, setAt: nowIso() },
      verification: { ...l.verification, verified: false },
    });
    await reload();
  }

  async function addLine() {
    const p = prices.find((x) => x.itemId === addItem);
    const qty = parseFloat(addQty);
    if (!p || !Number.isFinite(qty) || qty <= 0) { toast('Pick an item and a quantity first'); return; }
    await createRecord('takeoffLines', {
      jobId, priceItemId: p.itemId, qty, label: p.name.split('(')[0]!.trim(),
      provenance: { source: 'manual', setBy: user, setAt: nowIso() },
      verification: { verified: false },
    });
    setAddQty('');
    await reload();
  }

  async function removeLine(l: TakeoffLine) {
    await softDelete('takeoffLines', l.id);
    if (l.shapeId) await softDelete('shapes', l.shapeId);
    await reload();
    toast('Line removed (kept in history)');
  }

  /** The client is what a proposal is addressed to, so it belongs on the job, not
   *  in a notes field where a typo silently blanks the document (defect, 2026-07-27). */
  async function setClient(clientId: string) {
    await putRecord('jobs', { ...job, clientId: clientId || undefined });
    await Promise.all([reload(), refreshCore()]);
    const c = clientsSig.value.find((x) => x.id === clientId);
    toast(c ? `Job addressed to ${c.company || c.name}.` : 'Client cleared.');
  }

  async function setStatus(status: JobStatus) {
    await putRecord('jobs', { ...job, status });
    await Promise.all([reload(), refreshCore()]);
  }

  /**
   * Audit 2026-07-25: these three fields accepted any finite number, so -40 in
   * each produced a bid reading "Tax -$5,404.20" and "Deposit -$3,242.52" — a
   * document that would go to an HOA board saying we owe them money up front.
   *
   * Markup may be negative: a discount is a real thing a landscaper offers, and
   * the Adjustment line is unbounded for the same reason. Negative tax and a
   * deposit outside 0-100% are not things, so they are refused at the boundary
   * rather than clamped silently. compute.ts is untouched — the fixture math
   * stays byte-identical.
   */
  async function setFin(patch: Partial<typeof job.fin>) {
    if (patch.markupPct !== undefined && patch.markupPct < -100) {
      toast('A markup below -100% would make the job cost less than nothing.'); return;
    }
    if (patch.taxPct !== undefined && patch.taxPct < 0) {
      toast('Tax cannot be negative. Use the Adjustment line to take money off.'); return;
    }
    if (patch.depositPct !== undefined && (patch.depositPct < 0 || patch.depositPct > 100)) {
      toast('A deposit has to be between 0% and 100% of the total.'); return;
    }
    await putRecord('jobs', { ...job, fin: { ...job.fin, ...patch } });
    await reload();
  }

  async function finishDraft() {
    if (!armed) return;
    const pts = draftRef.current;
    if (armed.kind === 'boundary') {
      if (pts.length < 3) { toast('A boundary needs at least 3 corners — keep clicking the map'); return; }
      const fresh = (await getRecord('jobs', jobId))!;
      await putRecord('jobs', {
        ...fresh,
        parcel: {
          provider: 'manual', apn: '', rings: [pts],
          lotSqFt: Math.round(polygonAreaSqFt(pts)), perimeterFt: Math.round(perimeterFt(pts)),
          fetchedAt: nowIso(),
        },
      });
      updateDraft([]); setArmedKey('');
      await reload();
      toast('Boundary saved — lot area computed from your drawing');
      return;
    }
    const need = armed.priceKind === 'area' ? 3 : armed.priceKind === 'line' ? 2 : 1;
    if (pts.length < need) {
      toast(armed.priceKind === 'area' ? 'An area needs at least 3 corners' : armed.priceKind === 'line' ? 'A line needs at least 2 points' : 'Click the map once per item first');
      return;
    }
    const m = measureToQty(armed.priceKind, pts);
    const shape = await createRecord('shapes', {
      jobId, kind: armed.priceKind === 'area' ? 'polygon' : armed.priceKind === 'line' ? 'line' : 'points',
      coords: pts,
      measure: { value: m.qty, unit: m.unit, method: armed.priceKind === 'count' ? 'manual' : 'karney', computedAt: nowIso() },
    });
    await createRecord('takeoffLines', {
      jobId, priceItemId: armed.itemId, qty: m.qty,
      label: armed.name.split('(')[0]!.trim(), shapeId: shape.id,
      provenance: { source: 'manual', setBy: user, setAt: nowIso(), detail: 'map-draw' },
      verification: { verified: false },
    });
    updateDraft([]);
    await reload();
    toast(`${armed.name.split('(')[0]!.trim()}: ${fmtQty(m.qty, m.unit === 'sqft' ? 'sq ft' : m.unit === 'linft' ? 'lin ft' : 'each')} ${m.unit === 'sqft' ? 'sq ft' : m.unit === 'linft' ? 'lin ft' : 'items'} added to the takeoff`);
  }

  async function onBasemap(bm: string) {
    if (!settings) return;
    await putSettings({ ...settings, basemap: bm });
    await refreshCore();
  }

  async function onPickPoint(lat: number, lng: number) {
    setPicking(false);
    const fresh = (await getRecord('jobs', jobId))!;
    await putRecord('jobs', { ...fresh, lat, lng, zoom: fresh.zoom ?? 18 });
    try {
      const r = await lookupParcel(lat, lng);
      if (!r.ok) { await reload(); toast(r.message); return; }
      const again = (await getRecord('jobs', jobId))!;
      await putRecord('jobs', { ...again, parcel: r.parcel, siteAddress: again.siteAddress || r.parcel.address || '' });
      await reload();
      const sqft = r.parcel.lotSqFt.toLocaleString('en-US');
      // same defect as the search bar had: tapping inside an HOA lands on somebody's
      // house, and outlining it without saying so reads as "this is the job"
      toast(looksLikeOneHouse(r.parcel)
        ? `That is one lot — ${sqft} sq ft in ${r.parcel.subname}. For the whole community, press “The whole community”.`
        : `Outlined ${sqft} sq ft — APN ${r.parcel.apn}. Unverified until you walk it.`);
    } catch {
      await reload();
      toast('The county lookup failed. Drawing by hand still works.');
    }
  }

  async function onPinSite(lat: number, lng: number, zoom: number) {
    const fresh = (await getRecord('jobs', jobId))!;
    await putRecord('jobs', { ...fresh, lat, lng, zoom });
    await reload();
    toast('Site pin saved — the map opens here from now on');
  }

  const verifs = [...lines.map((l) => l.verification), ...inventory.map((i) => i.verification)];
  const center: LatLng = [job.lat ?? 33.4484, job.lng ?? -112.074];
  const zoom = job.zoom ?? (job.lat ? 19 : 11);
  const measureItems = prices.filter((p) => !p.retired && p.kind !== 'service');

  return (
    <div>
      <div class="pagehead">
        <div>
          <h1>{job.title}</h1>
          <div class="muted small">
            {job.siteAddress || 'No site address yet'} · <span class="mono">{job.bidNumber}</span>
            {job.parcel?.apn ? <span> · <span class="mono">APN {job.parcel.apn}</span></span> : null}
            {job.parcel?.label ? <span> · <b>{job.parcel.label}</b></span> : null}
            {job.parcel ? <span> · lot <span class="num">{job.parcel.lotSqFt.toLocaleString('en-US')}</span> sq ft{job.parcel.provider === 'manual' ? ' (hand-drawn boundary)' : job.parcel.provider === 'maricopa-subdivision' ? ' (whole recorded plat — confirm which tracts are the client\u2019s)' : ''}</span> : null}
          </div>
        </div>
        <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
          <FlagLine items={verifs} />
          <a class="btn btn-ghost" href={`#/field/${jobId}`} style="text-decoration:none;">Field mode</a>
          <select value={job.clientId ?? ''} aria-label="Client for this job"
            onChange={(e) => void setClient((e.target as HTMLSelectElement).value)}>
            <option value="">No client yet</option>
            {clientsSig.value.map((c) => (
              <option key={c.id} value={c.id}>{c.company || c.name}</option>
            ))}
          </select>
          <select value={job.status} onChange={(e) => void setStatus((e.target as HTMLSelectElement).value as JobStatus)} aria-label="Job status">
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      <div class="jobgrid">
        <section class="card" style="padding:10px;display:flex;flex-direction:column;gap:8px;" aria-label="Map and measurement">
          <FindBar job={job} picking={picking} onPicking={setPicking} onChanged={reload} />
          <TractFinder job={job} prices={prices} user={user} viewBox={viewBox} onChanged={reload} />
          <IntakeBar job={job} inventory={inventory} user={user} viewBox={viewBox}
            basemap={settings?.basemap ?? 'mc2026'} onChanged={reload} />
          <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
            <select value={armedKey} onChange={(e) => { setArmedKey((e.target as HTMLSelectElement).value); updateDraft([]); }} aria-label="Drawing tool" style="min-height:36px;">
              <option value="">Draw on map…</option>
              <option value="boundary">Property boundary (manual)</option>
              <optgroup label="Measured items">
                {measureItems.map((p) => <option key={p.itemId} value={p.itemId}>{p.name}</option>)}
              </optgroup>
            </select>
            {armed && (
              <>
                <span class="small muted" data-testid="draw-hint">
                  {draftPts.length === 0
                    ? (armed.kind === 'boundary' || armed.kind === 'item' && armed.priceKind === 'area' ? 'Click the map at each corner, then Finish.'
                      : armed.priceKind === 'line' ? 'Click along the run, then Finish.'
                        : 'Click once per item, then Finish.')
                    : `${draftPts.length} point${draftPts.length === 1 ? '' : 's'} so far`}
                </span>
                <button class="btn btn-small btn-primary" onClick={() => void finishDraft()}>Finish shape</button>
                <button class="btn btn-small btn-ghost" onClick={() => { updateDraft([]); setArmedKey(''); }}>Cancel</button>
              </>
            )}
          </div>
          {/* A tall map that will not shrink is what pushed the controls out of
              a fixed-height column (2026-07-27). It still asks for 480px on a
              roomy screen, but it gives way before the buttons above it do. */}
          <div style="flex:1 1 480px;min-height:320px;">
            <MapPane
              center={center} zoom={zoom}
              basemap={settings?.basemap ?? 'mc2026'} onBasemap={(bm) => void onBasemap(bm)}
              shapes={shapeViews} parcel={job.parcel}
              selectedShapeId={selectedShapeId} onSelectShape={setSelectedShapeId}
              picking={picking} onPickPoint={(la, ln) => void onPickPoint(la, ln)}
              onView={setViewBox}
              armed={armed} draftPts={draftPts}
              onMapClick={(pt) => updateDraft([...draftRef.current, pt])}
              onEscape={() => updateDraft([])}
              onPinSite={(la, ln, z) => void onPinSite(la, ln, z)}
            />
          </div>
          <p class="small muted" style="margin:0;">
            Areas and lengths are computed on the WGS84 ellipsoid. The scan finds only what OpenStreetMap volunteers mapped — every scanned row arrives unverified.
          </p>
        </section>

        <div style="display:flex;flex-direction:column;gap:16px;min-width:0;">
          <section class="card" aria-label="Takeoff">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:8px;">
              <h2>Takeoff</h2>
              <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
                <select value={addItem} onChange={(e) => setAddItem((e.target as HTMLSelectElement).value)} aria-label="Price book item to add" style="max-width:230px;min-height:36px;">
                  <option value="">Add by hand…</option>
                  {prices.filter((p) => !p.retired).map((p) => (
                    <option key={p.itemId} value={p.itemId}>{p.name}</option>
                  ))}
                </select>
                <input type="number" inputMode="decimal" placeholder="Qty" value={addQty} style="width:80px;min-height:36px;"
                  onInput={(e) => setAddQty((e.target as HTMLInputElement).value)} aria-label="Quantity" />
                <button class="btn btn-small btn-ghost" onClick={() => void addLine()}>Add line</button>
              </div>
            </div>

            {resolved.length === 0 ? (
              <Empty>No takeoff lines yet. Pick an item under <b>Draw on map…</b> and outline it on the aerial — the quantity computes itself. Or add a line by hand above.</Empty>
            ) : (
              <div style="overflow-x:auto;">
                <table class="data">
                  <thead>
                    <tr><th></th><th>Item</th><th class="money">Qty</th><th class="money">Rate</th><th class="money">Amount</th><th>Src</th><th></th></tr>
                  </thead>
                  <tbody>
                    {bid.lines.map((fl, i) => {
                      const rl = resolved[i]!;
                      const l = byId.get(rl.takeoffLineId)!;
                      const drawn = l.shapeId ? shapes.find((s) => s.id === l.shapeId) : undefined;
                      return (
                        <tr key={rl.takeoffLineId} class={l.shapeId && l.shapeId === selectedShapeId ? 'selected' : ''}
                          onClick={() => setSelectedShapeId(l.shapeId ?? null)}>
                          <td><Tie verified={fl.verified} onToggle={() => toggleVerify(l)} /></td>
                          <td>
                            <div style="font-weight:600;">{fl.name}</div>
                            {fl.label && fl.label !== fl.name ? <div class="muted small">{fl.label}</div> : null}
                            {drawn?.measure && drawn.measure.value !== fl.qty
                              ? <div class="small" style="color:var(--granite);">drawn: {fmtQty(drawn.measure.value, fl.unit)} — hand-edited since</div> : null}
                            {l.verification.note ? <div class="small" style="color:var(--paloverde);">{l.verification.note}</div> : null}
                          </td>
                          <td class="money">
                            <input type="number" inputMode="decimal" value={String(fl.qty)} style="width:84px;text-align:right;min-height:32px;padding:3px 6px;"
                              aria-label={`Quantity for ${fl.name}`}
                              onChange={(e) => void setQty(l, parseFloat((e.target as HTMLInputElement).value))} />
                            <div class="muted" style="font-size:11px;text-align:right;">{fl.unit}</div>
                          </td>
                          <td class="money num">{fmtCents(fl.rateCents)}</td>
                          <td class="money num">{fmtCents(fl.amountCents)}</td>
                          <td><SourceChip source={fl.source} /></td>
                          <td><button class="btn btn-small btn-ghost" onClick={(ev) => { ev.stopPropagation(); void removeLine(l); }} aria-label={`Remove ${fl.name}`}>×</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {orphans.length > 0 && (
              <p class="small" style="color:var(--ocotillo);margin-top:8px;">
                {orphans.length} line(s) reference a price book item that no longer exists and are excluded from the math. Re-add them or restore the item on the Prices page.
              </p>
            )}

            <div style="max-width:340px;margin-left:auto;margin-top:14px;" class="num" aria-label="Bid arithmetic">
              <Row k="Subtotal" v={bid.math.subtotalCents} />
              <RowEdit k="Overhead / markup" pct={job.fin.markupPct} v={bid.math.markupCents} onPct={(p) => void setFin({ markupPct: p })} />
              <RowEdit k="Adjustment ($)" flat={job.fin.adjustCents / 100} v={bid.math.adjustCents} onFlat={(d) => void setFin({ adjustCents: toCents(d) })} />
              <RowEdit k="Tax" pct={job.fin.taxPct} v={bid.math.taxCents} onPct={(p) => void setFin({ taxPct: p })} />
              <div style="display:flex;justify-content:space-between;border-top:2px solid var(--mesquite);margin-top:6px;padding-top:6px;font-weight:600;font-size:17px;">
                <span>Total</span><span>{fmtCents(bid.math.totalCents)}</span>
              </div>
              <RowEdit k="Deposit" pct={job.fin.depositPct} v={bid.math.depositCents} onPct={(p) => void setFin({ depositPct: p })} />
            </div>
          </section>

          <InventoryPanel jobId={job.id} items={inventory} user={user}
            onToggleVerify={toggleVerifyInv} onChanged={reload} />

          {settings && (
            <AiPanel job={job} lines={lines} inventory={inventory} prices={prices}
              review={b.review} user={user}
              configured={!!(settings.proxyUrl && settings.proxyPassphrase)}
              onChanged={reload} />
          )}

          {settings && (
            <DocsPanel job={job} lines={lines} inventory={inventory} prices={prices}
              settings={settings} documents={b.documents} onChanged={reload} />
          )}

          {job.notes ? (
            <section class="card"><h2 style="margin-bottom:8px;">Notes</h2><p style="white-space:pre-wrap;">{job.notes}</p></section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: number }) {
  return <div style="display:flex;justify-content:space-between;padding:3px 0;"><span>{k}</span><span>{fmtCents(v)}</span></div>;
}

function RowEdit(props: { k: string; v: number; pct?: number; flat?: number; onPct?: (p: number) => void; onFlat?: (d: number) => void }) {
  const { k, v, pct, flat, onPct, onFlat } = props;
  return (
    <div style="display:flex;justify-content:space-between;align-items:center;padding:2px 0;gap:8px;">
      <span style="display:inline-flex;align-items:center;gap:6px;">
        {k}
        <input
          type="number" inputMode="decimal" step="0.5"
          value={String(pct ?? flat ?? 0)}
          style="width:74px;min-height:30px;padding:2px 6px;text-align:right;"
          aria-label={k}
          onChange={(e) => {
            const n = parseFloat((e.target as HTMLInputElement).value);
            if (!Number.isFinite(n)) return;
            if (onPct) onPct(n); else if (onFlat) onFlat(n);
          }}
        />
        {pct !== undefined ? <span class="muted small">%</span> : null}
      </span>
      <span>{fmtCents(v)}</span>
    </div>
  );
}
